export interface RankCashBracket {
  fromRank: number
  toRank: number
  cashUsd: number
}

export interface RankTicketBracket {
  fromRank: number
  toRank: number
  tickets: number
}

export interface RafflePrize {
  label: string
  winners: number
  cashUsd?: number
  cashUsdMin?: number
  cashUsdMax?: number
}

export interface TierUnlock {
  points: number
  tickets: number
}

export interface MinesCashoutRow {
  safePicks: number
  multiplier: number
  survivalProbability?: number
}

export type LiveGamePrizeType = "points" | "bonus" | "item" | "free_spins" | "side_quest" | "nothing"

export interface LiveGamePrizeConfig {
  type: LiveGamePrizeType
  amount: number
  label: string
  probabilityPct: number
}

export interface WelcomeSpinPrizeConfig {
  amount: number
  label: string
  probabilityPct: number
}

export interface OvalClubSettings {
  version: string
  coreEconomy: {
    pointsPerDollar: number
    targetPointsPerRepPerDay: number
    defaultActiveReps: number
    systemDailyPointBudget: number
    seasonLengthMonths: number
    pointsResetEachSeason: boolean
    allTimeStatsNeverReset: boolean
    topLeaderboardCutoff: number
    weeklyTierReachPctMin: number
    weeklyTierReachPctMax: number
    rewardCreditConversionPctMin: number
    rewardCreditConversionPctMax: number
    rewardCreditCashConversionPct: number
    rewardCreditEvPerDealUsd: number
    maxCommunityEvPctOfProfit: number
  }
  cashControl: {
    cashNeverEqualsPoints: boolean
    pointsAreNotWithdrawable: boolean
    pointsUnlockOpportunities: boolean
    distributionChannels: {
      tierUnlocks: boolean
      leaderboardPlacement: boolean
      raffles: boolean
      specialEvents: boolean
    }
    placementEarnsTicketsNotGuaranteedCash: boolean
  }
  eventCadence: {
    weeklyLeaderboardEnabled: boolean
    monthlyLeaderboardEnabled: boolean
    seasonalLeaderboardEnabled: boolean
    kingOfTheHillCadence: "weekly" | "monthly" | "quarterly" | "seasonal"
    kingOfTheHillCanBeLimitedTo: "none" | "quarterly" | "seasonal"
    dailyWheelFreeSpinsPerDay: number
    checkInCanGrantExtraWheelSpins: boolean
  }
  tierEligibility: {
    weekly: TierUnlock[]
    monthly: TierUnlock[]
    seasonal: TierUnlock[]
  }
  leaderboards: {
    individual: {
      weekly: {
        topCutoff: number
        guaranteedCashBrackets: RankCashBracket[]
        rafflePotUsdMin: number
        rafflePotUsdMax: number
        placementTicketBrackets: RankTicketBracket[]
        rafflePrizes: RafflePrize[]
      }
      monthly: {
        topCutoff: number
        guaranteedCashBrackets: RankCashBracket[]
        rafflePotUsdMin: number
        rafflePotUsdMax: number
        placementTicketBrackets: RankTicketBracket[]
        rafflePrizes: RafflePrize[]
      }
      seasonal: {
        topCutoff: number
        guaranteedCashBrackets: RankCashBracket[]
        rafflePotUsdMin: number
        rafflePotUsdMax: number
        placementTicketBrackets: RankTicketBracket[]
        rafflePrizes: RafflePrize[]
      }
    }
    team: {
      weekly: {
        topCutoff: number
        guaranteedCashBrackets: RankCashBracket[]
        sharedRaffleTicketsEnabled: boolean
      }
      monthly: {
        topCutoff: number
        guaranteedCashBrackets: RankCashBracket[]
      }
      seasonal: {
        topCutoff: number
        guaranteedCashBrackets: RankCashBracket[]
        teamOnlyMegaRaffleEnabled: boolean
        prestigeRewards: string[]
      }
    }
  }
  qualificationGates: {
    individual: {
      weekly: { minDeals: number; minPoints: number }
      monthly: { minDeals: number; minPoints: number }
      seasonal: { minDeals: number; minPoints: number }
    }
    team: {
      weekly: { minMembersMeetingDealsPct: number; minDealsPerQualifiedMember: number; minTeamDealsPerMember: number }
      monthly: { minMembersMeetingDealsPct: number; minDealsPerQualifiedMember: number; minTeamDealsPerMember: number }
      seasonal: { minMembersMeetingDealsPct: number; minDealsPerQualifiedMember: number; minTeamDealsPerMember: number }
    }
    raffleEligibility: {
      weeklyRequiresIndividualMinimums: boolean
      seasonalRequiresIndividualMinimums: boolean
      placementWithoutQualificationGetsNoRewards: boolean
    }
    kingOfTheHill: {
      minActiveMembersPct: number
      minTeamDealsPerMember: number
      mustRepeatMinimumsInDefenseWeek: boolean
      mustFinishRankOneBothWeeks: boolean
      unclaimedIfNoQualifiedTeam: boolean
    }
  }
  kingOfTheHill: {
    enabled: boolean
    teamsOnly: boolean
    weeklyResetPoints: boolean
    noCarryoverPoints: boolean
    requiredConsecutiveWeeksAtRankOne: number
    dethronedChallengerMustDefendWeeks: number
    championPrizeOptions: Array<{
      label: string
      cashUsd?: number
      retailValueUsd?: number
      estimatedCostUsd?: number
    }>
    runnerUpConsolation: {
      cashUsd: number
      grantMassiveRaffleTickets: boolean
    }
    throttleControls: {
      allowRaiseDifficulty: boolean
      allowIncreaseParticipationRequirements: boolean
      allowLimitFrequency: boolean
    }
  }
  games: {
    slot: {
      wagers: number[]
      expectedReturnMultiplier: number
      houseEdgePct: number
      probabilities: Array<{ outcome: string; payoutMultiplier: number; probabilityPct: number }>
      dealRewardPrizes: LiveGamePrizeConfig[]
    }
    mines: {
      gridTiles: number
      houseEdgePct: number
      defaultMode: "easy" | "normal" | "hard"
      cashoutFormula: string
      modes: {
        easy: { mines: number; cashoutTable: MinesCashoutRow[] }
        normal: { mines: number; cashoutTable: MinesCashoutRow[] }
        hard: { mines: number; cashoutTable: MinesCashoutRow[] }
      }
    }
    wheel: {
      freeSpinsPerDay: number
      checkInCanGrantExtraSpin: boolean
      outcomes: Array<{
        outcome: string
        rewardType: "points" | "token"
        points?: number
        tokenMultiplier?: number
        tokenUses?: number
        tokenExpiryHours?: number
        probabilityPct: number
      }>
      freeSpinRewardPrizes: LiveGamePrizeConfig[]
      tokenAppliesToBaseDealPointsOnly: boolean
      tokensConsumableOnAnyDeal: boolean
      tokenExpiryHours: number
    }
    welcomeSpin: {
      prizes: WelcomeSpinPrizeConfig[]
    }
    guardrails: {
      pointsCanBeGambled: boolean
      rewardCreditsCanBeGambled: boolean
      wagersMustUseOwnedPoints: boolean
      multipliersAreConsumable: boolean
      multipliersExpireHours: number
      primaryThrottleLever: "mines" | "slot" | "wheel"
      primaryExcitementLever: "mines" | "slot" | "wheel"
    }
  }
  throttles: {
    burnTriggersPct: {
      normalMax: number
      softTightenMax: number
      hardTightenMax: number
    }
    normal: {
      slotUsesBaseTable: boolean
      minesMode: "normal" | "easy" | "hard"
      wheelUsesBaseTable: boolean
    }
    softTighten: {
      slotTopOutcomeMultiplier: number
      slotAffectedMultipliers: number[]
      slotRedistributeToMultipliers: number[]
      minesTargetMines: number
      minesCashoutReductionPctMin: number
      minesCashoutReductionPctMax: number
      wheelThreeXTokenPct: number
      wheelShiftToMissPct: number
    }
    hardTighten: {
      slotTwentyXMultiplier: number
      slotTenXMultiplier: number
      slotRedistributeToMultipliers: number[]
      minesTargetMines: number
      minesMaxPickDepthUiCap: number
      wheelJackpotPct: number
      wheelThreeXTokenPct: number
      wheelShiftToMissOrTwentyPtsPct: number
    }
    pointsStillIssuedWhenThrottled: boolean
  }
  integrity: {
    fraudDetectionEnabled: boolean
    velocityAnomalyFlagsEnabled: boolean
    manualBanWorkflowEnabled: boolean
    keepEnforcementSeparateFromEconomyRules: boolean
  }
  internalDashboards: {
    pointVelocityMetrics: string[]
    rewardCreditConversionMetrics: string[]
    repBehaviorHeatmapMetrics: string[]
  }
  uiLanguage: {
    qualificationMessages: string[]
    avoidTerms: string[]
  }
}

export const DEFAULT_OVAL_CLUB_SETTINGS: OvalClubSettings = {
  version: "1.5.0",
  coreEconomy: {
    pointsPerDollar: 10,
    targetPointsPerRepPerDay: 200,
    defaultActiveReps: 5000,
    systemDailyPointBudget: 1000000,
    seasonLengthMonths: 2,
    pointsResetEachSeason: true,
    allTimeStatsNeverReset: true,
    topLeaderboardCutoff: 500,
    weeklyTierReachPctMin: 2,
    weeklyTierReachPctMax: 3,
    rewardCreditConversionPctMin: 30,
    rewardCreditConversionPctMax: 40,
    rewardCreditCashConversionPct: 40,
    rewardCreditEvPerDealUsd: 4,
    maxCommunityEvPctOfProfit: 20,
  },
  cashControl: {
    cashNeverEqualsPoints: true,
    pointsAreNotWithdrawable: true,
    pointsUnlockOpportunities: true,
    distributionChannels: {
      tierUnlocks: true,
      leaderboardPlacement: true,
      raffles: true,
      specialEvents: true,
    },
    placementEarnsTicketsNotGuaranteedCash: true,
  },
  eventCadence: {
    weeklyLeaderboardEnabled: true,
    monthlyLeaderboardEnabled: true,
    seasonalLeaderboardEnabled: true,
    kingOfTheHillCadence: "weekly",
    kingOfTheHillCanBeLimitedTo: "quarterly",
    dailyWheelFreeSpinsPerDay: 1,
    checkInCanGrantExtraWheelSpins: true,
  },
  tierEligibility: {
    weekly: [
      { points: 600, tickets: 1 },
      { points: 1000, tickets: 3 },
      { points: 1500, tickets: 7 },
    ],
    monthly: [
      { points: 2500, tickets: 1 },
      { points: 4000, tickets: 5 },
      { points: 6000, tickets: 12 },
    ],
    seasonal: [
      { points: 5000, tickets: 2 },
      { points: 8000, tickets: 8 },
      { points: 12000, tickets: 20 },
    ],
  },
  leaderboards: {
    individual: {
      weekly: {
        topCutoff: 500,
        guaranteedCashBrackets: [
          { fromRank: 1, toRank: 1, cashUsd: 500 },
          { fromRank: 2, toRank: 5, cashUsd: 250 },
          { fromRank: 6, toRank: 20, cashUsd: 100 },
          { fromRank: 21, toRank: 100, cashUsd: 50 },
          { fromRank: 101, toRank: 500, cashUsd: 20 },
        ],
        rafflePotUsdMin: 10000,
        rafflePotUsdMax: 10000,
        placementTicketBrackets: [
          { fromRank: 1, toRank: 5, tickets: 100 },
          { fromRank: 6, toRank: 20, tickets: 50 },
          { fromRank: 21, toRank: 100, tickets: 20 },
          { fromRank: 101, toRank: 500, tickets: 5 },
        ],
        rafflePrizes: [
          { label: "weekly-grand", winners: 1, cashUsd: 3000 },
          { label: "weekly-tier-2", winners: 2, cashUsd: 1500 },
          { label: "weekly-tier-3", winners: 4, cashUsd: 1000 },
          { label: "weekly-tier-4", winners: 10, cashUsd: 200 },
        ],
      },
      monthly: {
        topCutoff: 500,
        guaranteedCashBrackets: [
          { fromRank: 1, toRank: 1, cashUsd: 2000 },
          { fromRank: 2, toRank: 5, cashUsd: 1000 },
          { fromRank: 6, toRank: 20, cashUsd: 500 },
          { fromRank: 21, toRank: 100, cashUsd: 200 },
          { fromRank: 101, toRank: 500, cashUsd: 75 },
        ],
        rafflePotUsdMin: 25000,
        rafflePotUsdMax: 40000,
        placementTicketBrackets: [
          { fromRank: 1, toRank: 5, tickets: 100 },
          { fromRank: 6, toRank: 20, tickets: 50 },
          { fromRank: 21, toRank: 100, tickets: 20 },
          { fromRank: 101, toRank: 500, tickets: 5 },
        ],
        rafflePrizes: [],
      },
      seasonal: {
        topCutoff: 500,
        guaranteedCashBrackets: [
          { fromRank: 1, toRank: 1, cashUsd: 7500 },
          { fromRank: 2, toRank: 5, cashUsd: 3000 },
          { fromRank: 6, toRank: 20, cashUsd: 1500 },
          { fromRank: 21, toRank: 100, cashUsd: 500 },
          { fromRank: 101, toRank: 500, cashUsd: 200 },
        ],
        rafflePotUsdMin: 75000,
        rafflePotUsdMax: 150000,
        placementTicketBrackets: [
          { fromRank: 1, toRank: 5, tickets: 150 },
          { fromRank: 6, toRank: 20, tickets: 75 },
          { fromRank: 21, toRank: 100, tickets: 30 },
          { fromRank: 101, toRank: 500, tickets: 10 },
        ],
        rafflePrizes: [
          { label: "seasonal-grand-prize", winners: 1, cashUsdMin: 25000, cashUsdMax: 50000 },
          { label: "seasonal-tier-2", winners: 4, cashUsd: 10000 },
          { label: "seasonal-tier-3", winners: 8, cashUsd: 5000 },
        ],
      },
    },
    team: {
      weekly: {
        topCutoff: 50,
        guaranteedCashBrackets: [
          { fromRank: 1, toRank: 1, cashUsd: 2500 },
          { fromRank: 2, toRank: 3, cashUsd: 1500 },
          { fromRank: 4, toRank: 10, cashUsd: 750 },
          { fromRank: 11, toRank: 50, cashUsd: 250 },
        ],
        sharedRaffleTicketsEnabled: true,
      },
      monthly: {
        topCutoff: 50,
        guaranteedCashBrackets: [
          { fromRank: 1, toRank: 1, cashUsd: 10000 },
          { fromRank: 2, toRank: 3, cashUsd: 5000 },
          { fromRank: 4, toRank: 10, cashUsd: 2500 },
          { fromRank: 11, toRank: 50, cashUsd: 750 },
        ],
      },
      seasonal: {
        topCutoff: 10,
        guaranteedCashBrackets: [
          { fromRank: 1, toRank: 1, cashUsd: 25000 },
          { fromRank: 2, toRank: 3, cashUsd: 12500 },
          { fromRank: 4, toRank: 10, cashUsd: 5000 },
        ],
        teamOnlyMegaRaffleEnabled: true,
        prestigeRewards: ["banners", "badges", "irl-perks"],
      },
    },
  },
  qualificationGates: {
    individual: {
      weekly: { minDeals: 5, minPoints: 600 },
      monthly: { minDeals: 20, minPoints: 2500 },
      seasonal: { minDeals: 45, minPoints: 5000 },
    },
    team: {
      weekly: { minMembersMeetingDealsPct: 60, minDealsPerQualifiedMember: 3, minTeamDealsPerMember: 5 },
      monthly: { minMembersMeetingDealsPct: 70, minDealsPerQualifiedMember: 10, minTeamDealsPerMember: 20 },
      seasonal: { minMembersMeetingDealsPct: 80, minDealsPerQualifiedMember: 20, minTeamDealsPerMember: 45 },
    },
    raffleEligibility: {
      weeklyRequiresIndividualMinimums: true,
      seasonalRequiresIndividualMinimums: true,
      placementWithoutQualificationGetsNoRewards: true,
    },
    kingOfTheHill: {
      minActiveMembersPct: 80,
      minTeamDealsPerMember: 10,
      mustRepeatMinimumsInDefenseWeek: true,
      mustFinishRankOneBothWeeks: true,
      unclaimedIfNoQualifiedTeam: true,
    },
  },
  kingOfTheHill: {
    enabled: true,
    teamsOnly: true,
    weeklyResetPoints: true,
    noCarryoverPoints: true,
    requiredConsecutiveWeeksAtRankOne: 2,
    dethronedChallengerMustDefendWeeks: 2,
    championPrizeOptions: [
      { label: "cash", cashUsd: 50000 },
      { label: "luxury-trip", retailValueUsd: 75000, estimatedCostUsd: 40000 },
      { label: "vehicle-or-experiential-prize" },
    ],
    runnerUpConsolation: {
      cashUsd: 5000,
      grantMassiveRaffleTickets: true,
    },
    throttleControls: {
      allowRaiseDifficulty: true,
      allowIncreaseParticipationRequirements: true,
      allowLimitFrequency: true,
    },
  },
  games: {
    slot: {
      wagers: [25, 50, 100],
      expectedReturnMultiplier: 0.92,
      houseEdgePct: 8,
      probabilities: [
        { outcome: "bust", payoutMultiplier: 0, probabilityPct: 33.61 },
        { outcome: "half_back", payoutMultiplier: 0.5, probabilityPct: 38.723 },
        { outcome: "push", payoutMultiplier: 1, probabilityPct: 16.946 },
        { outcome: "double", payoutMultiplier: 2, probabilityPct: 5.37 },
        { outcome: "big_win", payoutMultiplier: 5, probabilityPct: 3.424 },
        { outcome: "mega", payoutMultiplier: 10, probabilityPct: 1.072 },
        { outcome: "jackpot", payoutMultiplier: 20, probabilityPct: 0.855 },
      ],
      dealRewardPrizes: [
        { type: "nothing", amount: 0, label: "No reward", probabilityPct: 18 },
        { type: "points", amount: 10, label: "10 Points", probabilityPct: 22 },
        { type: "points", amount: 25, label: "25 Points", probabilityPct: 17 },
        { type: "points", amount: 50, label: "50 Points", probabilityPct: 7 },
        { type: "points", amount: 100, label: "100 Points", probabilityPct: 2 },
        { type: "bonus", amount: 1, label: "$1 Bonus Cash", probabilityPct: 10 },
        { type: "bonus", amount: 2, label: "$2 Bonus Cash", probabilityPct: 6 },
        { type: "bonus", amount: 5, label: "$5 Bonus Cash", probabilityPct: 4 },
        { type: "bonus", amount: 10, label: "$10 Bonus Cash", probabilityPct: 1 },
        { type: "free_spins", amount: 1, label: "1 Free Spin", probabilityPct: 4 },
        { type: "free_spins", amount: 2, label: "2 Free Spins", probabilityPct: 2 },
        { type: "item", amount: 1, label: "Oval Shirt", probabilityPct: 0.8 },
        { type: "item", amount: 1, label: "Oval Hat", probabilityPct: 0.7 },
        { type: "item", amount: 1, label: "Oval Water Bottle", probabilityPct: 0.5 },
        { type: "side_quest", amount: 0, label: "Side Quest!", probabilityPct: 5 },
      ],
    },
    mines: {
      gridTiles: 25,
      houseEdgePct: 8,
      defaultMode: "normal",
      cashoutFormula: "(1 - house_edge) / survival_probability",
      modes: {
        easy: {
          mines: 3,
          cashoutTable: [
            { safePicks: 1, multiplier: 1.05 },
            { safePicks: 2, multiplier: 1.19 },
            { safePicks: 3, multiplier: 1.37 },
            { safePicks: 4, multiplier: 1.59 },
            { safePicks: 5, multiplier: 1.86 },
            { safePicks: 6, multiplier: 2.18 },
            { safePicks: 7, multiplier: 2.59 },
            { safePicks: 8, multiplier: 3.11 },
          ],
        },
        normal: {
          mines: 5,
          cashoutTable: [
            { safePicks: 1, survivalProbability: 0.8, multiplier: 1.15 },
            { safePicks: 2, survivalProbability: 0.6333, multiplier: 1.45 },
            { safePicks: 3, survivalProbability: 0.4957, multiplier: 1.86 },
            { safePicks: 4, survivalProbability: 0.383, multiplier: 2.4 },
            { safePicks: 5, survivalProbability: 0.2918, multiplier: 3.15 },
            { safePicks: 6, survivalProbability: 0.2189, multiplier: 4.2 },
            { safePicks: 7, survivalProbability: 0.1613, multiplier: 5.7 },
            { safePicks: 8, survivalProbability: 0.1165, multiplier: 7.9 },
          ],
        },
        hard: {
          mines: 8,
          cashoutTable: [
            { safePicks: 1, multiplier: 1.35 },
            { safePicks: 2, multiplier: 2.03 },
            { safePicks: 3, multiplier: 3.11 },
            { safePicks: 4, multiplier: 4.89 },
            { safePicks: 5, multiplier: 7.9 },
            { safePicks: 6, multiplier: 13.17 },
            { safePicks: 7, multiplier: 22.74 },
            { safePicks: 8, multiplier: 40.93 },
          ],
        },
      },
    },
    wheel: {
      freeSpinsPerDay: 1,
      checkInCanGrantExtraSpin: true,
      outcomes: [
        { outcome: "miss", rewardType: "points", points: 0, probabilityPct: 10 },
        { outcome: "small", rewardType: "points", points: 20, probabilityPct: 25 },
        { outcome: "medium", rewardType: "points", points: 40, probabilityPct: 20 },
        { outcome: "nice", rewardType: "points", points: 60, probabilityPct: 15 },
        { outcome: "solid", rewardType: "points", points: 100, probabilityPct: 10 },
        { outcome: "token_1_5x", rewardType: "token", tokenMultiplier: 1.5, tokenUses: 1, tokenExpiryHours: 24, probabilityPct: 10 },
        { outcome: "token_2x", rewardType: "token", tokenMultiplier: 2, tokenUses: 1, tokenExpiryHours: 24, probabilityPct: 7 },
        { outcome: "token_3x", rewardType: "token", tokenMultiplier: 3, tokenUses: 1, tokenExpiryHours: 24, probabilityPct: 2 },
        { outcome: "jackpot", rewardType: "points", points: 500, probabilityPct: 1 },
      ],
      freeSpinRewardPrizes: [
        { type: "nothing", amount: 0, label: "No reward", probabilityPct: 18 },
        { type: "points", amount: 10, label: "10 Points", probabilityPct: 22 },
        { type: "points", amount: 25, label: "25 Points", probabilityPct: 17 },
        { type: "points", amount: 50, label: "50 Points", probabilityPct: 7 },
        { type: "points", amount: 100, label: "100 Points", probabilityPct: 2 },
        { type: "bonus", amount: 1, label: "$1 Bonus Cash", probabilityPct: 10 },
        { type: "bonus", amount: 2, label: "$2 Bonus Cash", probabilityPct: 6 },
        { type: "bonus", amount: 5, label: "$5 Bonus Cash", probabilityPct: 4 },
        { type: "bonus", amount: 10, label: "$10 Bonus Cash", probabilityPct: 1 },
        { type: "free_spins", amount: 1, label: "1 Free Spin", probabilityPct: 4 },
        { type: "free_spins", amount: 2, label: "2 Free Spins", probabilityPct: 2 },
        { type: "item", amount: 1, label: "Oval Shirt", probabilityPct: 0.8 },
        { type: "item", amount: 1, label: "Oval Hat", probabilityPct: 0.7 },
        { type: "item", amount: 1, label: "Oval Water Bottle", probabilityPct: 0.5 },
        { type: "side_quest", amount: 0, label: "Side Quest!", probabilityPct: 5 },
      ],
      tokenAppliesToBaseDealPointsOnly: true,
      tokensConsumableOnAnyDeal: true,
      tokenExpiryHours: 24,
    },
    welcomeSpin: {
      prizes: [
        { amount: 100, label: "JACKPOT!", probabilityPct: 2 },
        { amount: 50, label: "BIG WIN!", probabilityPct: 5 },
        { amount: 20, label: "GREAT!", probabilityPct: 10 },
        { amount: 10, label: "NICE!", probabilityPct: 15 },
        { amount: 5, label: "GOOD!", probabilityPct: 20 },
        { amount: 2, label: "WIN!", probabilityPct: 23 },
        { amount: 1, label: "WIN!", probabilityPct: 25 },
      ],
    },
    guardrails: {
      pointsCanBeGambled: true,
      rewardCreditsCanBeGambled: false,
      wagersMustUseOwnedPoints: true,
      multipliersAreConsumable: true,
      multipliersExpireHours: 24,
      primaryThrottleLever: "mines",
      primaryExcitementLever: "slot",
    },
  },
  throttles: {
    burnTriggersPct: {
      normalMax: 70,
      softTightenMax: 90,
      hardTightenMax: 100,
    },
    normal: {
      slotUsesBaseTable: true,
      minesMode: "normal",
      wheelUsesBaseTable: true,
    },
    softTighten: {
      slotTopOutcomeMultiplier: 0.75,
      slotAffectedMultipliers: [10, 20],
      slotRedistributeToMultipliers: [0, 0.5],
      minesTargetMines: 6,
      minesCashoutReductionPctMin: 3,
      minesCashoutReductionPctMax: 5,
      wheelThreeXTokenPct: 1,
      wheelShiftToMissPct: 1,
    },
    hardTighten: {
      slotTwentyXMultiplier: 0.4,
      slotTenXMultiplier: 0.6,
      slotRedistributeToMultipliers: [0, 0.5],
      minesTargetMines: 8,
      minesMaxPickDepthUiCap: 8,
      wheelJackpotPct: 0.25,
      wheelThreeXTokenPct: 0.5,
      wheelShiftToMissOrTwentyPtsPct: 2.25,
    },
    pointsStillIssuedWhenThrottled: true,
  },
  integrity: {
    fraudDetectionEnabled: true,
    velocityAnomalyFlagsEnabled: true,
    manualBanWorkflowEnabled: true,
    keepEnforcementSeparateFromEconomyRules: true,
  },
  internalDashboards: {
    pointVelocityMetrics: ["points_per_minute", "daily_budget_consumed_pct"],
    rewardCreditConversionMetrics: ["points_to_credits_rate", "credits_to_cash_vs_non_cash"],
    repBehaviorHeatmapMetrics: ["engagement_vs_production", "multiplier_usage", "game_participation"],
  },
  uiLanguage: {
    qualificationMessages: ["Not yet qualified", "One more deal to qualify", "2 deals away from rewards eligibility"],
    avoidTerms: ["disqualified"],
  },
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

const deepMergeKnownShape = (base: unknown, updates: unknown): unknown => {
  if (Array.isArray(base)) {
    return Array.isArray(updates) ? updates : base
  }

  if (isPlainObject(base)) {
    if (!isPlainObject(updates)) {
      return base
    }

    const merged: Record<string, unknown> = {}
    for (const [key, baseValue] of Object.entries(base)) {
      merged[key] = deepMergeKnownShape(baseValue, updates[key])
    }

    return merged
  }

  if (typeof base === "number") {
    return typeof updates === "number" && Number.isFinite(updates) ? updates : base
  }

  if (typeof base === "boolean") {
    return typeof updates === "boolean" ? updates : base
  }

  if (typeof base === "string") {
    return typeof updates === "string" ? updates : base
  }

  return updates ?? base
}

export function cloneOvalClubSettings(settings: OvalClubSettings): OvalClubSettings {
  return structuredClone(settings)
}

export function cloneDefaultOvalClubSettings(): OvalClubSettings {
  return cloneOvalClubSettings(DEFAULT_OVAL_CLUB_SETTINGS)
}

export function mergeOvalClubSettings(base: OvalClubSettings, updates: unknown): OvalClubSettings {
  return deepMergeKnownShape(cloneOvalClubSettings(base), updates) as OvalClubSettings
}

export function normalizeOvalClubSettings(input: unknown): OvalClubSettings {
  return mergeOvalClubSettings(cloneDefaultOvalClubSettings(), input)
}
